import { z } from "zod";

// Twin of @libs/wire's event-time module (kept local — the two wire packages are
// independent contracts). Event display time, 2026-07-23 operator decision: the DB
// stores the true instant + the venue's IANA zone; the API resolves them into
// venue-local wall-clock COMPONENTS once, server-side, and clients render them
// literally — no timezone conversion, no precision flags. `time: null` = the source
// only published a date.
export const eventStartLocalSchema = z.object({
  /** venue-local calendar date, `YYYY-MM-DD` */
  date: z.string(),
  /** venue-local 24h clock, `HH:MM`; null = date-only (render no time) */
  time: z.string().nullable(),
});
export type EventStartLocal = z.infer<typeof eventStartLocalSchema>;

/** Resolve a stored event instant into venue-local wall-clock components. `timezone`
 *  null = zone unknown; the stored value IS the wall clock stamped as UTC, so UTC
 *  formatting reads it back verbatim. Date-only rows always resolve the date in UTC
 *  (they store the venue date's UTC midnight). */
export function toEventStartLocal(
  value: string | Date,
  timezone: string | null | undefined,
  timeKnown: boolean,
): EventStartLocal {
  const instant = value instanceof Date ? value : new Date(value);
  const zone = timeKnown ? (timezone ?? "UTC") : "UTC";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(instant);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "00";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: timeKnown ? `${get("hour").replace(/^24$/, "00")}:${get("minute")}` : null,
  };
}

/** With no stored end, an event counts as passed this long after it starts. */
export const EVENT_PASSED_WITHOUT_END_MS = 60 * 60 * 1000;
/** A date-only event stores its date's UTC midnight: it lasts that day. */
const EVENT_DATE_ONLY_MS = 24 * 60 * 60 * 1000;

/** Whether an event is over: after its stored end; with none, an hour after
 *  its start, or at the end of its day when only the date is known. Tickets
 *  stop being offered once it is; whether the vendor still sells before then
 *  is learnt from a purchase, not the clock. */
export function eventHasPassed(
  event: {
    startsAt: string | Date;
    endsAt?: string | Date | null;
    /** False when only the date was published; absent means known. */
    startTimeKnown?: boolean;
  },
  now: number,
): boolean {
  const end = event.endsAt
    ? new Date(event.endsAt).getTime()
    : new Date(event.startsAt).getTime() +
      (event.startTimeKnown === false
        ? EVENT_DATE_ONLY_MS
        : EVENT_PASSED_WITHOUT_END_MS);
  return end <= now;
}
